import { useCallback, useMemo, useState } from "react";
import { User } from "@/types/User.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast.ts";
import { queryKeys } from "@/services/queryKeyFactory.ts";
import { deleteUser, getUsers } from "@/services/userService.ts";
import { getUsersColumns } from "@/pages/users/users-columns.tsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card.tsx";
import { PageTitle } from "@/components/ui/headings";
import UserForm from "@/pages/users/UserForm.tsx";
import { DataTable } from "@/components/data-table/data-table.tsx";
import { ToolbarOptions } from "@/types/data-table/ToolbarOptions.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { getTrendSummary } from "@/services/suggestionService.ts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart.tsx";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { getSeasons } from "@/services/seasonService.ts";

const UsersPage = () => {
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: users, isFetching } = useQuery({
        queryKey: queryKeys.fetchUsers.all,
        queryFn: () => getUsers(),
        initialData: [],
    });

    const { data: seasons } = useQuery({
        queryKey: queryKeys.seasons.all,
        queryFn: () => getSeasons(),
        initialData: [],
    });

    const { data: trendSummary } = useQuery({
        queryKey: queryKeys.fetchSummary.trends,
        queryFn: () => getTrendSummary(),
        initialData: [],
    });

    // Active season: isActive or first in list. Last season: max season id < active season id (seasons ordered by id).
    const { currentSummary, lastSummary, userCountsBySeason, mentorDelta, mentorPercent, menteeDelta, menteePercent } = useMemo(() => {
        if (!trendSummary || !seasons || trendSummary.length === 0) {
            return {
                activeSeason: null,
                lastSeason: null,
                currentSummary: null,
                lastSummary: null,
                userCountsBySeason: [],
                mentorDelta: 0,
                mentorPercent: 0,
                menteeDelta: 0,
                menteePercent: 0,
            };
        }
        const seasonsByIdAsc = [...seasons].sort((a, b) => a.id - b.id);
        const activeSeason = seasons.find((s: { isActive?: boolean }) => s.isActive) ?? seasons[0];
        const lastSeason = seasonsByIdAsc.filter((s: { id: number }) => s.id < activeSeason.id).pop() ?? null;
        const currentSummary = trendSummary.find((s) => s.seasonId === activeSeason.id) ?? null;
        const lastSummary = lastSeason ? trendSummary.find((s) => s.seasonId === lastSeason.id) ?? null : null;

        const currentMentors = currentSummary?.mentorCount ?? 0;
        const currentMentees = currentSummary?.menteeCount ?? 0;
        const lastMentors = lastSummary?.mentorCount ?? 0;
        const lastMentees = lastSummary?.menteeCount ?? 0;
        const mentorDelta = currentMentors - lastMentors;
        const menteeDelta = currentMentees - lastMentees;
        const mentorPercent = lastMentors === 0 ? (currentMentors > 0 ? 100 : 0) : Math.round((mentorDelta / lastMentors) * 100);
        const menteePercent = lastMentees === 0 ? (currentMentees > 0 ? 100 : 0) : Math.round((menteeDelta / lastMentees) * 100);

        const chartRows: { seasonName: string; totalUsers: number; mentorCount: number; menteeCount: number }[] = [];
        if (lastSummary && lastSeason) {
            chartRows.push({
                seasonName: lastSeason.name,
                totalUsers: (lastSummary.mentorCount ?? 0) + (lastSummary.menteeCount ?? 0),
                mentorCount: lastSummary.mentorCount ?? 0,
                menteeCount: lastSummary.menteeCount ?? 0,
            });
        }
        if (currentSummary && activeSeason) {
            chartRows.push({
                seasonName: activeSeason.name,
                totalUsers: (currentSummary.mentorCount ?? 0) + (currentSummary.menteeCount ?? 0),
                mentorCount: currentSummary.mentorCount ?? 0,
                menteeCount: currentSummary.menteeCount ?? 0,
            });
        }
        return {
            activeSeason,
            lastSeason,
            currentSummary,
            lastSummary,
            userCountsBySeason: chartRows,
            mentorDelta,
            mentorPercent,
            menteeDelta,
            menteePercent,
        };
    }, [trendSummary, seasons]);

    const deleteMutation = useMutation({
        mutationFn: deleteUser,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.fetchUsers.all });
        },
    });

    const onDelete = useCallback((user: User) => {
        setUserToDelete(user);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (!userToDelete) return;
        deleteMutation.mutate(userToDelete.id, {
            onSuccess: () => {
                setUserToDelete(null);
                toast({ description: t("user.deleteSuccess") });
            },
            onError: () => {
                toast({
                    variant: "destructive",
                    title: t("common.errorTitle"),
                    description: t("common.errorDesc"),
                });
            },
        });
    }, [userToDelete, deleteMutation, toast, t]);

    const handleCancelDelete = useCallback(() => {
        setUserToDelete(null);
    }, []);

    const onEdit = useCallback((user: User) => {
        setSelectedUser(user);
        setIsDialogOpen(true);
    }, []);

    const toolbarOptions: ToolbarOptions = {
        filters: [
            {
                column: "role",
                title: "role"
            }
        ],
        searchableColumn: "email"
    }

    const columns = useMemo(
        () => getUsersColumns({ onEdit, onDelete }),
        [onEdit, onDelete]
    );
    return (
        <>
        <div className="container max-w-7xl py-12">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_440px]">
                <div className="space-y-2">
                    <PageTitle>{t("user.all")}</PageTitle>
                </div>
                <div className="flex justify-end items-start">
                    <UserForm
                        isOpen={isDialogOpen}
                        user={selectedUser}
                        onOpenChange={(value) => {
                            setIsDialogOpen(value);
                            if (!value) {
                                setSelectedUser(null);
                            }
                        }}
                    />
                </div>
                {/* Table - Left Column */}
                <Card className={`border border-border bg-bg-card shadow-sm rounded-xl ${userCountsBySeason.length > 0 ? "lg:col-span-1" : "lg:col-span-2"}`}>
                        <CardContent className="pt-6">
                            {isFetching &&
                                <div className="flex flex-col space-y-3">
                                    <div className="space-y-2">
                                        <Skeleton className="h-8 w-full"/>
                                        <Skeleton className="h-8 w-full"/>
                                        <Skeleton className="h-8 w-full"/>
                                        <Skeleton className="h-8 w-full"/>
                                        <Skeleton className="h-8 w-full"/>
                                        <Skeleton className="h-8 w-full"/>
                                    </div>
                                </div>
                            }
                            {!isFetching && <DataTable data={users} columns={columns} id="users" toolbarOptions={toolbarOptions} />}
                        </CardContent>
                    </Card>

                    {/* Chart - Right Column */}
                    {userCountsBySeason.length > 0 && (
                        <Card className="border border-border bg-bg-card shadow-sm rounded-xl">
                            <CardHeader className="border-b border-border">
                                <CardTitle className="text-lg font-semibold text-text-1">
                                    {t("user.seasonComparison", "User Growth Since Last Season")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-6">
                                {/* Stat cards: same layout as mentee/mentor dashboard (title, big value, badge %, footer) */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Card className="border border-border bg-bg-card shadow-sm rounded-xl @container/card">
                                        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
                                            <div className="space-y-2">
                                                <CardDescription className="text-text-2">{t("common.mentor")}</CardDescription>
                                                <CardTitle className="text-5xl font-semibold tabular-nums text-text-1">
                                                    {(currentSummary?.mentorCount ?? 0).toLocaleString()}
                                                </CardTitle>
                                            </div>
                                            {lastSummary != null && (
                                                <Badge
                                                    variant="outline"
                                                    className={`shrink-0 gap-1 ${
                                                        mentorPercent >= 0
                                                            ? "border-status-success/50 text-status-success"
                                                            : "border-status-danger/50 text-status-danger"
                                                    }`}
                                                >
                                                    {mentorPercent >= 0 ? (
                                                        <TrendingUp className="size-3.5" />
                                                    ) : (
                                                        <TrendingDown className="size-3.5" />
                                                    )}
                                                    {mentorPercent >= 0
                                                        ? `+${Math.abs(mentorPercent)}%`
                                                        : `-${Math.abs(mentorPercent)}%`}
                                                </Badge>
                                            )}
                                        </CardHeader>
                                        <CardFooter className="flex-col items-start gap-2 text-sm">
                                            <p className="text-muted-foreground">
                                                {lastSummary == null
                                                    ? t("user.noPreviousSeason")
                                                    : mentorDelta >= 0
                                                        ? t("user.increasedSinceLastSeason", { count: Math.abs(mentorDelta) })
                                                        : t("user.decreasedSinceLastSeason", { count: Math.abs(mentorDelta) })}
                                            </p>
                                        </CardFooter>
                                    </Card>
                                    <Card className="border border-border bg-bg-card shadow-sm rounded-xl @container/card">
                                        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
                                            <div className="space-y-2">
                                                <CardDescription className="text-text-2">{t("common.mentee")}</CardDescription>
                                                <CardTitle className="text-5xl font-semibold tabular-nums text-text-1">
                                                    {(currentSummary?.menteeCount ?? 0).toLocaleString()}
                                                </CardTitle>
                                            </div>
                                            {lastSummary != null && (
                                                <Badge
                                                    variant="outline"
                                                    className={`shrink-0 gap-1 ${
                                                        menteePercent >= 0
                                                            ? "border-status-success/50 text-status-success"
                                                            : "border-status-danger/50 text-status-danger"
                                                    }`}
                                                >
                                                    {menteePercent >= 0 ? (
                                                        <TrendingUp className="size-3.5" />
                                                    ) : (
                                                        <TrendingDown className="size-3.5" />
                                                    )}
                                                    {menteePercent >= 0
                                                        ? `+${Math.abs(menteePercent)}%`
                                                        : `-${Math.abs(menteePercent)}%`}
                                                </Badge>
                                            )}
                                        </CardHeader>
                                        <CardFooter className="flex-col items-start gap-2 text-sm">
                                            <p className="text-muted-foreground">
                                                {lastSummary == null
                                                    ? t("user.noPreviousSeason")
                                                    : menteeDelta >= 0
                                                        ? t("user.increasedSinceLastSeason", { count: Math.abs(menteeDelta) })
                                                        : t("user.decreasedSinceLastSeason", { count: Math.abs(menteeDelta) })}
                                            </p>
                                        </CardFooter>
                                    </Card>
                                </div>
                                <div className="[&_.recharts-legend-item-text]:!text-text-1">
                                    <ChartContainer 
                                        className="h-[300px] w-full" 
                                        config={{
                                            mentorCount: { label: t("common.mentor"), color: "#3EC2FF" },
                                            menteeCount: { label: t("common.mentee"), color: "#2BE081" },
                                        }}
                                    >
                                        <BarChart data={userCountsBySeason} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                                            <XAxis 
                                                dataKey="seasonName" 
                                                tickLine={false} 
                                                axisLine={false} 
                                                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }} 
                                            />
                                            <YAxis 
                                                tickLine={false} 
                                                axisLine={false} 
                                                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }} 
                                                width={40}
                                            />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            <Legend 
                                                wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
                                            />
                                            <Bar 
                                                dataKey="mentorCount" 
                                                name={t("common.mentor")}
                                                fill="var(--color-mentorCount)" 
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Bar 
                                                dataKey="menteeCount" 
                                                name={t("common.mentee")}
                                                fill="var(--color-menteeCount)" 
                                                radius={[4, 4, 0, 0]}
                                            />
                                        </BarChart>
                                    </ChartContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}
            </div>
        </div>

        <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {userToDelete && (
                            <>
                                {t("user.deleteConfirmDesc")}
                                {userToDelete.fullName && (
                                    <span className="mt-2 block font-medium text-foreground">
                                        {t("common.delete")}: {userToDelete.fullName}
                                    </span>
                                )}
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={handleCancelDelete} disabled={deleteMutation.isPending}>
                        {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            handleConfirmDelete();
                        }}
                        disabled={deleteMutation.isPending}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {deleteMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t("common.loading")}
                            </>
                        ) : (
                            t("common.delete")
                        )}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        </>
    );
};

export default UsersPage;